/**
 * Click-to-open dropdown: one open menu at a time, a click outside or Escape closes it.
 *
 * Listeners live on the triggers themselves. The two document-level listeners — outside click and
 * Escape — exist ONLY while a menu is open: opening attaches them, closing removes them. Nothing
 * is left bound to the document once every menu is shut, and `destroy()` unbinds the triggers too.
 *
 * WHY A SECOND IMPLEMENTATION EXISTS
 * The legacy twin is `contextMenu()` in `webroot/js/new/custom.js`, which drives every existing
 * `.dropdown-menu` on the LMS. Both scripts load on the same pages, so this class deliberately
 * uses ITS OWN hooks — sharing `.dropdown-menu`/`.show` would have both listeners fire on one
 * click, the first opening the menu and the second closing it again.
 *
 * Redesigned markup opts in with the hooks below; untouched screens keep the legacy one. Once the
 * last `.dropdown-menu` has migrated, delete `contextMenu()` and its call in that file's
 * DOMContentLoaded block — not before.
 *
 * Markup contract:
 *
 *   <div class="js-dropdown">
 *       <button type="button" class="js-dropdown-trigger" aria-expanded="false">Export</button>
 *       <ul class="js-dropdown-list"> … items … </ul>
 *   </div>
 *
 * An item may opt into closing the menu when it is clicked by carrying `js-dropdown-close`. Opt-IN,
 * not the default: most items here are `data-popup` links or downloads that leave the menu where the
 * reader left it, and the one case that needs it is an item which opens a SLIDE-OVER — a menu left
 * standing open beside a panel reads as a second live surface, and `Panel.lockBackground()` has
 * already made it inert, so it would be visible and dead.
 *
 * The open state lands on the root as `is-opened`; styling is entirely the host component's job.
 * One measurement is made for the host: a list that would open past the viewport's left edge (a
 * right-anchored menu whose trigger has wrapped to the left of the page) also marks the root
 * `is-flip-inline`, and the host decides what that means.
 * Triggers are collected once, on construction — markup injected later needs its own instance.
 *
 * `aria-expanded` is kept in step below, and markup ships it `false` so it is already right before this
 * script runs. It is the only ARIA the pattern needs: the list follows its trigger in the DOM, which is
 * a disclosure. `aria-haspopup` would promise a `role="menu"` with arrow-key navigation and a roving
 * tabindex, and these are plain lists of links reached with Tab — so it stays off.
 *
 * @param {object} [options]
 * @param {string} [options.rootClass]    root hook, default `js-dropdown`
 * @param {string} [options.triggerClass] trigger hook, default `js-dropdown-trigger`
 * @param {string} [options.openClass]    state class set on the root, default `is-opened`
 * @param {HTMLElement|Document} [options.context] where to collect triggers from, default `document`.
 *        Markup injected into a panel needs its own instance scoped to that panel — a second
 *        document-wide instance would bind the host page's triggers a second time and every click
 *        would open and immediately re-close.
 */
export default class DropdownToggle {
    constructor(options = {}) {
        this.rootClass = options.rootClass || 'js-dropdown';
        this.triggerClass = options.triggerClass || 'js-dropdown-trigger';
        this.openClass = options.openClass || 'is-opened';

        // Deliberately NOT an option: an outer shell has to recognise an open menu without knowing
        // which `rootClass` this instance was given, so every instance must mark itself the same way.
        this.openHook = 'js-dropdown-open';
        // Likewise deliberately fixed: the markup declares it, so every instance must honour the
        // same hook.
        this.closeHook = 'js-dropdown-close';
        this.flipClass = 'is-flip-inline';
        this.context = options.context || document;

        this.openRoot = null;
        this.triggers = [];

        this.$onTriggerClick = this.onTriggerClick.bind(this);
        this.$onOutsideClick = this.onOutsideClick.bind(this);
        this.$onKeyDown = this.onKeyDown.bind(this);

        this.init();
    }

    init() {
        this.triggers = [...this.context.querySelectorAll(`.${this.triggerClass}`)];
        this.triggers.forEach((trigger) => trigger.addEventListener('click', this.$onTriggerClick));
    }

    /**
     * @param {MouseEvent} event
     */
    onTriggerClick(event) {
        const root = event.currentTarget.closest(`.${this.rootClass}`);

        if (!root) {
            return;
        }

        // Triggers are buttons, but an anchor would otherwise jump the page.
        event.preventDefault();

        if (root === this.openRoot) {
            this.close();

            return;
        }

        this.open(root);
    }

    /**
     * @param {HTMLElement} root
     */
    open(root) {
        this.close();

        root.classList.add(this.openClass, this.openHook);
        this.openRoot = root;

        const list = root.querySelector('.js-dropdown-list');

        if (list && list.getBoundingClientRect().left < 0) {
            root.classList.add(this.flipClass);
        }

        const trigger = root.querySelector(`.${this.triggerClass}`);

        if (trigger) {
            trigger.setAttribute('aria-expanded', 'true');
        }

        // Safe to bind during the click that opened the menu: the handler below ignores anything
        // inside the open root, and the trigger is inside it.
        document.addEventListener('click', this.$onOutsideClick);
        document.addEventListener('keydown', this.$onKeyDown);
    }

    /**
     * @param {boolean} [restoreFocus] Send focus back to the trigger — for Escape, where the
     *                                 keyboard user would otherwise be left with nothing focused.
     */
    close(restoreFocus = false) {
        if (!this.openRoot) {
            return;
        }

        const trigger = this.openRoot.querySelector(`.${this.triggerClass}`);

        this.openRoot.classList.remove(this.openClass, this.openHook, this.flipClass);
        this.openRoot = null;

        document.removeEventListener('click', this.$onOutsideClick);
        document.removeEventListener('keydown', this.$onKeyDown);

        if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');

            if (restoreFocus) {
                trigger.focus();
            }
        }
    }

    /**
     * @param {MouseEvent} event
     */
    onOutsideClick(event) {
        // A click inside the open menu belongs to the item that was clicked, not to the toggle —
        // unless that item asked for the menu to go with it.
        if (this.openRoot && this.openRoot.contains(event.target)) {
            if (event.target.closest(`.${this.closeHook}`)) {
                this.close();
            }

            return;
        }

        this.close();
    }

    /**
     * @param {KeyboardEvent} event
     */
    onKeyDown(event) {
        if (event.key === 'Escape') {
            this.close(true);
        }
    }

    destroy() {
        this.close();
        this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.$onTriggerClick));
        this.triggers = [];
    }
}
